/**
 * InviteOrgMemberForm — email + role, used in the Org Settings Teams modal.
 *
 * The server is the authoritative gate (server/api-util/orgPermissions.js).
 * `actorRole` is used here only to hide roles the actor can't grant, so the
 * UI doesn't show options that will server-reject.
 *
 * Spec: UPDATE.md §3.6.8
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';

import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import { ROLES, ROLE_LABELS, canInviteRole } from '../../util/orgRoles';
import {
  Form,
  FieldTextInput,
  FieldSelect,
  SecondaryButton,
  PrimaryButton,
} from '../../components';

import css from './InviteOrgMemberForm.module.css';

const INVITABLE_ROLES = [ROLES.ADMIN, ROLES.MANAGER, ROLES.MEMBER];

const ROLE_HINTS = {
  [ROLES.ADMIN]: 'Full org access except billing & deletion.',
  [ROLES.MANAGER]: 'Manage shows, episodes, and audience tools.',
  [ROLES.MEMBER]: 'View dashboards and insights.',
};

const InviteOrgMemberFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        className,
        rootClassName,
        handleSubmit,
        intl,
        invalid,
        values,
        inviteInProgress,
        inviteError,
        actorRole,
        existingEmails,
        onCancel,
      } = fieldRenderProps;

      const allowedRoles = INVITABLE_ROLES.filter(r =>
        actorRole ? canInviteRole(actorRole, r) : true
      );

      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'InviteOrgMemberForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'InviteOrgMemberForm.emailInvalid',
        })
      );
      const uniqueEmail = validators.uniqueEmail(
        intl.formatMessage({
          id: 'InviteOrgMemberForm.emailDuplicate',
        }),
        existingEmails || []
      );

      const roleRequired = validators.required(
        intl.formatMessage({
          id: 'InviteOrgMemberForm.roleRequired',
        })
      );

      const classes = classNames(rootClassName || css.root, className);
      const submitDisabled = invalid || inviteInProgress;
      const roleHint = values?.role ? ROLE_HINTS[values.role] : null;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <FieldTextInput
            id="InviteOrgMemberForm.email"
            name="email"
            type="email"
            label={intl.formatMessage({
              id: 'InviteOrgMemberForm.emailLabel',
            })}
            placeholder={intl.formatMessage({
              id: 'InviteOrgMemberForm.emailPlaceholder',
            })}
            validate={validators.composeValidators(emailRequired, emailValid, uniqueEmail)}
          />

          <div>
            <FieldSelect
              id="InviteOrgMemberForm.role"
              name="role"
              label={intl.formatMessage({
                id: 'InviteOrgMemberForm.roleLabel',
              })}
              validate={roleRequired}
            >
              <option value="" disabled>
                {intl.formatMessage({
                  id: 'InviteOrgMemberForm.rolePlaceholder',
                })}
              </option>
              {allowedRoles.map(r => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </FieldSelect>
            {roleHint ? <p className={css.roleHint}>{roleHint}</p> : null}
          </div>

          {inviteError ? (
            <p className={css.error}>
              {inviteError.code === 'TIER_LIMIT_REACHED' ? (
                <FormattedMessage
                  id="InviteOrgMemberForm.tierLimitReached"
                  values={{ limit: inviteError.limit }}
                />
              ) : (
                <FormattedMessage id="InviteOrgMemberForm.submitFailed" />
              )}
            </p>
          ) : null}

          <div className={css.actions}>
            <SecondaryButton type="button" onClick={onCancel} disabled={inviteInProgress}>
              <FormattedMessage id="InviteOrgMemberForm.cancel" />
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              inProgress={inviteInProgress}
              inProgressClassName={css.submitButtonInProgress}
              disabled={submitDisabled}
            >
              <FormattedMessage id="InviteOrgMemberForm.submit" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

InviteOrgMemberFormComponent.defaultProps = {
  className: null,
  rootClassName: null,
  inviteError: null,
  actorRole: null,
  existingEmails: [],
};

const { bool, func, string, arrayOf } = PropTypes;

InviteOrgMemberFormComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  inviteInProgress: bool,
  inviteError: PropTypes.any,
  actorRole: string,
  existingEmails: arrayOf(string),
};

const InviteOrgMemberForm = compose(injectIntl)(InviteOrgMemberFormComponent);
InviteOrgMemberForm.displayName = 'InviteOrgMemberForm';

export default InviteOrgMemberForm;
